import { useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import Form from '@gef-ui/form-material/Form';

import { ComponentLoader } from 'components';
import { USER_ROLES } from 'constants/general';
import MODALS from 'constants/modals';
import {
	MPU_NOTIF_LOSSSME,
	MPU_NOTIF_PUSH_MARKETPLACE,
	MPU_NOTIF_SAN_MARCO,
	MPU_NOTIF_SMS,
	MPU_NOTIF_TASK_CREATION_FAIL,
	MPU_NOTIF_TASKCOPY_USER,
	MPU_NOTIF_USER,
	MPU_PUSH_NOTIF,
} from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { fetchNotificationSettings } from 'features/views/settings/actions';
import formDescriptorFn, { SETTINGS_EMAILS_FORM_NAME } from 'features/views/settings/form';
import { getRoles } from 'features/views/user/selectors';
import { fetchContextUserInfo } from 'features/views/userContext/actions';
import { getContextUser, getContextUserHasRole, hasContextPermissionById } from 'features/views/userContext/selectors';

import CustomSettingsModal from '../modals/CustomSettingsModal';
import AlwaysTurnedOnNotifications from './AlwaysTurnedOnNotifications';
import EObchodnikNotifications from './EObchodnikNotifications';
import LossClientSMETasksReport from './LossClientSMETasksReport';
import SmsNotifications from './SmsNotifications';
import SummaryNotifications from './SummaryNotifications';
import TaskCreationFailByEmail from './TaskCreationFailByEmail';
import TasksAndInformationByEmail from './TasksAndInformationByEmail';
import TasksAndInformationForSanMarco from './TasksAndInformationForSanMarco';

export const NotificationSendingTab = () => {
	const dispatch = useDispatch();

	const contextUser = useSelector(getContextUser);

	useEffect(() => {
		if (contextUser) {
			dispatch(fetchContextUserInfo());
		}
		dispatch(fetchNotificationSettings({ resetEmailForm: true }));
	}, [dispatch, contextUser]);

	const userSummaryNotifPerm = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_USER));
	const contextUserSummaryNotifPerm = useSelector((state) => hasContextPermissionById(state, MPU_NOTIF_USER));
	const hasSummaryNotifPermission = contextUser ? contextUserSummaryNotifPerm : userSummaryNotifPerm;

	const userTaskAndInfoByEmailPermission = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_TASKCOPY_USER));
	const contextUserTaskAndInfoByEmailPermission = useSelector((state) =>
		hasContextPermissionById(state, MPU_NOTIF_TASKCOPY_USER),
	);
	const hasTaskAndInfoByEmailPermission = contextUser
		? contextUserTaskAndInfoByEmailPermission
		: userTaskAndInfoByEmailPermission;

	const userTaskAndInfoForSanMarcoPermission = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_SAN_MARCO));
	const contextUserTaskAndInfoForSanMarcoPermission = useSelector((state) =>
		hasContextPermissionById(state, MPU_NOTIF_SAN_MARCO),
	);
	const hasTaskAndInfoForSanMarcoPermission = contextUser
		? contextUserTaskAndInfoForSanMarcoPermission
		: userTaskAndInfoForSanMarcoPermission;

	const isContextUserSeller = useSelector((state) => getContextUserHasRole(state, USER_ROLES.SELLER));
	const isContextUserPPZ = useSelector((state) => getContextUserHasRole(state, USER_ROLES.PPZ));
	const mainUserRoles = useSelector(getRoles);
	const isMainUserSellerOrPPz = useMemo(
		() => includes(USER_ROLES.SELLER, mainUserRoles) || includes(USER_ROLES.PPZ, mainUserRoles),
		[mainUserRoles],
	);

	const userHasSellerOrPPZRole = useMemo(() => {
		if (contextUser) {
			return isContextUserSeller || isContextUserPPZ;
		} else {
			return isMainUserSellerOrPPz;
		}
	}, [contextUser, isContextUserPPZ, isContextUserSeller, isMainUserSellerOrPPz]);

	const userEObchodnikPushNotifPerm = useSelector((state) => hasPermissionByIds(state)(MPU_PUSH_NOTIF));
	const userEObchodnikPushNotifMarketPlacePerm = useSelector((state) =>
		hasPermissionByIds(state)(MPU_NOTIF_PUSH_MARKETPLACE),
	);
	const userHasEObchodnikPerm =
		userEObchodnikPushNotifPerm || userEObchodnikPushNotifMarketPlacePerm || userHasSellerOrPPZRole;

	const contextUserEObchodnikPushNotifPerm = useSelector((state) => hasContextPermissionById(state, MPU_PUSH_NOTIF));
	const contextUserEObchodnikPushNotifMarketPlacePerm = useSelector((state) =>
		hasContextPermissionById(state, MPU_NOTIF_PUSH_MARKETPLACE),
	);
	const contextUserHasEObchodnikPerm =
		contextUserEObchodnikPushNotifPerm || contextUserEObchodnikPushNotifMarketPlacePerm || userHasSellerOrPPZRole;

	const hasEObchodnikPushNotifPermission = contextUser ? contextUserHasEObchodnikPerm : userHasEObchodnikPerm;

	const userSmsNotificationsPerm = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_SMS));
	const contextUserSmsNotificationsPerm = useSelector((state) => hasContextPermissionById(state, MPU_NOTIF_SMS));
	const hasSmsNotificationPermission = contextUser ? contextUserSmsNotificationsPerm : userSmsNotificationsPerm;

	const userTaskCreationFailPerm = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_TASK_CREATION_FAIL));
	const contextUserTaskCreationFailPerm = useSelector((state) =>
		hasContextPermissionById(state, MPU_NOTIF_TASK_CREATION_FAIL),
	);
	const hasTaskCreationFailPermission = contextUser ? contextUserTaskCreationFailPerm : userTaskCreationFailPerm;

	const userTaskLossSMEPerm = useSelector((state) => hasPermissionByIds(state)(MPU_NOTIF_LOSSSME));
	const contextUserTaskLossSMEPerm = useSelector((state) => hasContextPermissionById(state, MPU_NOTIF_LOSSSME));
	const hasTaskLossSMEPermission = contextUser ? contextUserTaskLossSMEPerm : userTaskLossSMEPerm;

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.userNotificationSettings}>
			<ComponentLoader fetchStorePath={fetch.constants.urls.userNotificationSettingsEmail}>
				<Paper sx={{ p: 3, borderRadius: 2 }}>
					<CustomSettingsModal id={MODALS.custom_settings} />
					<Form name={SETTINGS_EMAILS_FORM_NAME} formDescriptorFn={formDescriptorFn}>
						<Grid container spacing={5}>
							{hasSummaryNotifPermission && <SummaryNotifications />}
							{hasTaskAndInfoByEmailPermission && <TasksAndInformationByEmail />}
							{hasTaskAndInfoForSanMarcoPermission && <TasksAndInformationForSanMarco />}
							{hasEObchodnikPushNotifPermission && <EObchodnikNotifications />}
							{hasSmsNotificationPermission && <SmsNotifications />}
							{hasTaskCreationFailPermission && <TaskCreationFailByEmail />}
							{hasTaskLossSMEPermission && <LossClientSMETasksReport />}
							<AlwaysTurnedOnNotifications />
						</Grid>
					</Form>
				</Paper>
			</ComponentLoader>
		</ComponentLoader>
	);
};

export default NotificationSendingTab;
